import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/authContext'
import { LoginFormData, LoginFormErrors } from '../types/auth'
import Button from '../common/button'
import Input from '../common/input'
import Checkbox from '../common/checkbox'
import theme from '../config/theme'
import { validateEmail, validateEmailWithMessage, validatePassword } from '../utils/validation'
import { ApiError } from '../utils/apiClient'
import { getErrorMessage } from '../utils/errorMessages'
import config from '../config/config'

const DEBUG_MODE = true

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, isLoading } = useAuth()
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
    rememberMe: false
  })
  const [errors, setErrors] = useState<LoginFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const appVersion = config.appVersion
  const appName = config.appName

  // Redirect if already authenticated
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name as keyof LoginFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
    
    // Clear general error when user starts typing
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: undefined
      }))
    }
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const newErrors: LoginFormErrors = { ...errors }

    // Validate email on blur
    if (name === 'username') {
      if (!value || !value.trim()) {
        newErrors.username = 'กรุณากรอกอีเมล'
      } else {
        const emailValidation = validateEmailWithMessage(value)
        if (!emailValidation.valid) {
          newErrors.username = emailValidation.error || 'กรุณากรอกอีเมลที่ถูกต้อง'
        } else {
          delete newErrors.username
        }
      }
    }

    // Validate password on blur
    if (name === 'password') {
      if (!value || !value.trim()) {
        newErrors.password = 'กรุณากรอกรหัสผ่าน'
      } else if (value.trim().length < 6) {
        newErrors.password = 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'
      } else {
        delete newErrors.password
      }
    }

    setErrors(newErrors)
  }

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {}
    
    // Validate email: check empty first, then format
    if (!formData.username || !formData.username.trim()) {
      newErrors.username = 'กรุณากรอกอีเมล'
    } else {
      const emailValidation = validateEmailWithMessage(formData.username)
      if (!emailValidation.valid) {
        newErrors.username = emailValidation.error || 'กรุณากรอกอีเมลที่ถูกต้อง'
      }
    }

    // Validate password: check empty first, then minimum length
    if (!formData.password || !formData.password.trim()) {
      newErrors.password = 'กรุณากรอกรหัสผ่าน'
    } else if (formData.password.trim().length < 6) {
      newErrors.password = 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setErrors({})

    try {
      const success = await login({
        email: formData.username, // username field contains email
        password: formData.password
      })

      if (success) {
        navigate('/rich-menu')
      } else {
        setErrors({ general: 'กรุณาตรวจสอบอีเมลหรือรหัสผ่านเข้าใช้งาน' })
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.isAuthenticationError()) {
          setErrors({ general: 'กรุณาตรวจสอบอีเมลหรือรหัสผ่านเข้าใช้งาน' })
        } else {
          setErrors({ general: getErrorMessage(error) })
        }
      } else {
        setErrors({ general: 'กรุณาตรวจสอบอีเมลหรือรหัสผ่านเข้าใช้งาน' })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ backgroundColor: theme.brandColor.red }}
    >
      <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-between gap-8">
        {/* Login Card */}
        <div className="w-full max-w-md lg:max-w-lg">
          <div
            className="rounded-2xl p-6 sm:p-8 shadow-2xl animate-fade-in"
            style={{
              backgroundColor: theme.color.light100,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Logo */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4">
                <img
                  src="/images/logo.png"
                  alt="CP Logo"
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2" style={{ color: theme.brandColor.red }}>Smart PC LINE Solution</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <p className="text-base sm:text-lg mt-4 mb-0 text-center" style={{ color: theme.color.gray400 }}>Hi, Welcome!</p>

              <Input
                type="email"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder="Enter your email address"
                disabled={isSubmitting}
                label="Email Address"
                error={!!errors.username}
                errorMessage={errors.username}
                isValid={!errors.username && formData.username.length > 0 && validateEmail(formData.username)}
              />

              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder="Enter your password"
                disabled={isSubmitting}
                label="Password"
                error={!!errors.password}
                errorMessage={errors.password}
                isValid={!errors.password && formData.password.length > 0 && formData.password.trim().length >= 6}
                showPasswordToggle={true}
              />

              <Checkbox
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                disabled={isSubmitting}
                label="Remember Me"
                centered
              />
              <div className="flex flex-col items-center justify-center w-full">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  <span className="inline-block min-w-[120px]">
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Logging in...
                      </span>
                    ) : (
                      'Log in'
                    )}
                  </span>
                </Button>
                <div className='text-center text-gray-400 text-xs mt-2 italic'>{appName} {appVersion}</div>
                {errors.general && (
                  <div
                    className="mt-3 text-center text-xs"
                    style={{
                      color: theme.color.error
                    }}
                  >
                    {errors.general}
                  </div>
                )}
              </div>
            </form>

           
          </div>
        </div>

        {/* Mascot */}
        <div className="hidden lg:block lg:mt-0 flex-1 max-w-md">
          <div className="relative top-8 flex justify-center items-center">
            <img
              src="/images/mascot.png"
              alt="Support Mascot"
              className="max-w-full max-h-100 object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage